import { useState, useEffect, useCallback } from 'react';
import { HiArrowRight, HiArrowLeft } from 'react-icons/hi';
import './Carousel.css';

const isExternal = (url) => /^https?:\/\//i.test(url);

const Carousel = ({ slides }) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  const nextSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev === slides.length - 1 ? 0 : prev + 1));
  }, [slides.length]);

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev === 0 ? slides.length - 1 : prev - 1));
  };

  const goToSlide = (index) => {
    setCurrentSlide(index);
  };

  useEffect(() => {
    if (!isAutoPlaying) return undefined;

    const interval = setInterval(() => {
      nextSlide();
    }, 5000);

    return () => clearInterval(interval);
  }, [isAutoPlaying, nextSlide]);

  return (
    <section
      className="carousel-container"
      aria-roledescription="carousel"
      aria-label="Featured highlights"
      onMouseEnter={() => setIsAutoPlaying(false)}
      onMouseLeave={() => setIsAutoPlaying(true)}
    >
      <div className="carousel-wrapper">
        {slides.map((slide, index) => (
          <div
            key={slide.title}
            className={`carousel-slide ${index === currentSlide ? 'active' : ''}`}
            style={{
              backgroundImage: `url(${slide.image})`,
              transform: `translateX(${100 * (index - currentSlide)}%)`,
            }}
            role="group"
            aria-roledescription="slide"
            aria-label={`${index + 1} of ${slides.length}`}
            aria-hidden={index !== currentSlide}
          >
            <div className="carousel-content">
              <h3>{slide.title}</h3>
              <p>{slide.description}</p>
              <a
                href={slide.buttonLink}
                className="carousel-cta"
                target={isExternal(slide.buttonLink) ? '_blank' : undefined}
                rel={isExternal(slide.buttonLink) ? 'noopener noreferrer' : undefined}
              >
                {slide.buttonText}
              </a>
            </div>
          </div>
        ))}
      </div>

      <button
        type="button"
        className="carousel-nav prev"
        onClick={prevSlide}
        aria-label="Previous slide"
      >
        <HiArrowLeft />
      </button>
      <button
        type="button"
        className="carousel-nav next"
        onClick={nextSlide}
        aria-label="Next slide"
      >
        <HiArrowRight />
      </button>

      <div className="carousel-dots" role="tablist" aria-label="Slide navigation">
        {slides.map((slide, index) => (
          <button
            key={slide.title}
            type="button"
            role="tab"
            aria-selected={index === currentSlide}
            aria-label={`Go to slide ${index + 1}: ${slide.title}`}
            className={`dot ${index === currentSlide ? 'active' : ''}`}
            onClick={() => goToSlide(index)}
          />
        ))}
      </div>
    </section>
  );
};

export default Carousel;
